import { signIn, signUp, signOut, getCurrentUser, confirmSignIn, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import type { LoginFormData, RegisterFormData, SignInResult } from '@/types/auth';

export type SignInStep = 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' | 'EMAIL_OTP' | 'RESET_PASSWORD' | 'DONE' | 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';

export const authService = {
  async signUp({ email, password }: RegisterFormData) {
    const { isSignUpComplete, userId, nextStep } = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
        },
        autoSignIn: true,
      },
    });

    return { isSignUpComplete, userId, nextStep };
  },

  async signIn({ email }: LoginFormData): Promise<SignInResult> {
    const { isSignedIn, nextStep } = await signIn({
      username: email,
      options: {
        authFlowType: 'USER_AUTH',
        preferredChallenge: 'EMAIL_OTP',
      },
    });
    
    // Passwordless flow handling - Cognito returns EMAIL_OTP as ChallengeName
    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      return { 
        isSignedIn: false, 
        nextStep: 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' as SignInStep,
        email: email
      };
    }

    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { 
        isSignedIn: false, 
        nextStep: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' as SignInStep,
        email: email
      };
    }

    if (isSignedIn) {
      const attributes = await this.getUserAttributes();
      return { 
        isSignedIn, 
        nextStep: 'DONE' as SignInStep,
        userAttributes: attributes,
        email: email
      }; 
    }

    return { isSignedIn, nextStep: 'DONE' as SignInStep, email: email };
  },

  async confirmSignInWithOtp({ code }: { code: string }): Promise<SignInResult> {
    // Following the documentation: confirmSignIn with challengeResponse
    const { nextStep: confirmSignInNextStep } = await confirmSignIn({
      challengeResponse: code,
    });

    if (confirmSignInNextStep.signInStep === 'DONE') {
      const attributes = await this.getUserAttributes();
      return { 
        isSignedIn: true, 
        nextStep: 'DONE' as SignInStep,
        userAttributes: attributes
      };
    }

    return { isSignedIn: false, nextStep: 'DONE' as SignInStep };
  },

  async confirmSignInWithNewPassword(newPassword: string) {
    await confirmSignIn({
      challengeResponse: newPassword,
    });
    return true;
  },

  async signOut() {
    await signOut();
  },

  async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch {
      return null;
    }
  },

  async getUserAttributes() {
    try {
      return await fetchUserAttributes();
    } catch {
      return null;
    }
  },

  async refreshUserAttributes() {
    try {
      const attributes = await fetchUserAttributes();
      return attributes;
    } catch (error) {
      console.error('Error fetching user attributes:', error);
      return null;
    }
  },

  async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error fetching ID token:', error);
      return null;
    }
  },
}; 